import { defineStore } from 'pinia'
import { apiService } from '@/services/api.js'
import { ANALYTICS_EVENTS } from '@/constants/analyticsEvents.ts'
import analytics from '@/services/analytics.ts'

export const useMatchInviteStore = defineStore('matchInvite', {
    state: () => ({
        invite: null,
        isVisible: false,
        isLoading: false,
        action: null,
        autoDeclineSeconds: 30,
        autoDeclineTimer: null,
    }),

    getters: {
        hasInvite: (state) => !!state.invite,
        isModalVisible: (state) => state.isVisible && !!state.invite,
    },

    actions: {
        showInvite(inviteData) {
            try {
                // Once in production
                // Deffo need to make this work
                // We dont want any invites during a match
                // For demo for now disabled
                this.invite = inviteData
                this.isVisible = true

                // Every path that opens an invite lands here - the inbox list, a
                // toast, a deep link - so this is the one place that sees it.
                analytics.trackEvent(ANALYTICS_EVENTS.NOTIFICATION_OPENED, {
                    type: inviteData?.type ?? 'match_invite',
                })

                // if (!matchStore.setup.id && !matchStore.match.id) {
                //     // this.startAutoDeclineTimer()
                //     console.log('Match invite received:', inviteData)
                // } else {
                //     const matchName = inviteData.data?.match?.name || inviteData.match?.name || 'Unknown Match'
                //     console.log(
                //         `Match invite received but user is already in a match: ${matchName}`,
                //     )
                // }
            } catch (error) {
                console.error('Error handling match invite:', error)
                this.invite = inviteData
                this.isVisible = true
                // this.startAutoDeclineTimer()
            }
        },

        hideInvite() {
            this.isVisible = false
            this.invite = null
            this.clearAutoDeclineTimer()
        },

        hideInviteModal() {
            this.isVisible = false
            this.clearAutoDeclineTimer()
        },

        showInviteModal() {
            this.isVisible = true
        },

        clearInvite() {
            this.invite = null
            this.isVisible = false
            this.clearAutoDeclineTimer()
        },

        startAutoDeclineTimer() {
            this.autoDeclineSeconds = 30
            this.autoDeclineTimer = setInterval(() => {
                this.autoDeclineSeconds--
                if (this.autoDeclineSeconds <= 0) {
                    this.declineInvite()
                }
            }, 1000)
        },

        clearAutoDeclineTimer() {
            if (this.autoDeclineTimer) {
                clearInterval(this.autoDeclineTimer)
                this.autoDeclineTimer = null
            }
            this.autoDeclineSeconds = 30
        },

        $reset() {
            this.clearAutoDeclineTimer()
            this.invite = null
            this.isVisible = false
            this.isLoading = false
            this.action = null
            this.autoDeclineSeconds = 30
        },

        async acceptInvite(selectedSetupId) {
            if (this.isLoading || !this.invite) return

            this.isLoading = true
            this.action = 'accept'

            try {
                const matchId = this.invite.data?.match?.id || this.invite.match?.id
                const notificationId = this.invite.id

                await apiService.post(`/api/v1/matches/${matchId}/accept-invite`, {
                    notification_id: notificationId,
                    dart_setup_id: selectedSetupId || null,
                })

                analytics.trackEvent(ANALYTICS_EVENTS.MATCH_INVITE_ACCEPTED, {
                    match_id: String(matchId),
                    with_setup: !!selectedSetupId,
                })

                this.clearInvite()
                return true
            } catch (error) {
                console.error('Failed to accept match invite:', error)
                throw error
            } finally {
                this.isLoading = false
                this.action = null
            }
        },

        async declineInvite() {
            if (this.isLoading || !this.invite) return

            this.isLoading = true
            this.action = 'decline'

            try {
                const matchId = this.invite.data?.match?.id || this.invite.match?.id
                const notificationId = this.invite.id

                await apiService.post(`/api/v1/matches/${matchId}/decline-invite`, {
                    notification_id: notificationId,
                })

                analytics.trackEvent(ANALYTICS_EVENTS.MATCH_INVITE_DECLINED, {
                    match_id: String(matchId),
                })

                this.clearInvite()
                return true
            } catch (error) {
                console.error('Failed to decline match invite:', error)
                throw error
            } finally {
                this.isLoading = false
                this.action = null
            }
        },

        closeModal() {
            this.clearAutoDeclineTimer()
            this.hideInvite()
        },

        closeInviteIfMatch(matchId) {
            if (!this.invite) return

            const inviteMatchId = this.invite.data?.match?.id || this.invite.match?.id
            if (inviteMatchId === matchId) {
                this.clearInvite()
            }
        },
    },
})
